import { FC, useState } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import {
  DividendPayout,
  DividendPayoutItem,
  DividendPayoutType,
} from '../../../hooks/dividend/dividend.types';
import { DividendHooks } from '../../../hooks/index';
import { useShareClasses } from '../../../hooks/share-class/use-share-classes';
import { useInfiniteStakeholders } from '../../../hooks/stakeholder/use-infinite-stakeholders';
import { StakeholderOrder } from '../../../types/stakeholder.types';
import { getTranslation } from '../../../utils/get-translation.util';
import {
  DividendReviewPayoutModal,
  ReviewPayoutRow,
} from '../add-dividend/dividend-review-payout-modal';
import {
  getPeriodCovered,
  SummaryHighlights,
  SummaryRow,
  SummarySection,
} from '../add-dividend/dividend-summary-parts';
import { formatDividendAmount } from '../utils/format-dividend-amount.util';

type Props = {
  companyId: string;
  currencySymbol: string;
  isOpen: boolean;
  onClose: () => void;
  payout: DividendPayout | null;
};

const EMPTY_VALUE = '—';

export const DividendDetailsSheet: FC<Props> = ({
  companyId,
  isOpen,
  onClose,
  payout,
  currencySymbol,
}) => {
  const [reviewOpen, setReviewOpen] = useState(false);
  const { shareClasses } = useShareClasses({ companyId, filter: 'all', take: 200 });
  const { records: payoutRows } = DividendHooks.useDividendPayoutRows({
    companyId,
    enabled: isOpen && !!payout,
    payoutId: payout?.id ?? '',
  });
  const { exportPayout, isExportingPayout } = DividendHooks.useDividendExport();
  const { stakeholders } = useInfiniteStakeholders({
    companyId,
    search: '',
    sortOrder: StakeholderOrder.ASC,
  });

  if (!payout) return null;

  const isShareClass = payout.type === DividendPayoutType.PER_SHARE_CLASS;
  const periodCovered = getPeriodCovered({
    frequency: payout.frequency,
    periodIndex: payout.periodIndex,
    year: payout.year,
  });

  const formatAmount = (value: number) => `${currencySymbol} ${formatDividendAmount(value)}`;

  const shareClassName = (id: null | string) =>
    shareClasses.find((shareClass) => shareClass.id === id)?.name ?? EMPTY_VALUE;

  const stakeholderName = (id: null | string) =>
    stakeholders.find((stakeholder) => stakeholder.id === id)?.fullName ?? EMPTY_VALUE;

  // A per-share-class item stores no total: it is the class's shares times the per-share amount.
  const itemPayout = (item: DividendPayoutItem): number => {
    if (item.totalAmount !== null) return item.totalAmount;
    if (!isShareClass || !item.amountPerShare) return 0;

    const totalShares =
      shareClasses.find((shareClass) => shareClass.id === item.shareClassId)?.totalShares ?? 0;
    return totalShares * item.amountPerShare;
  };

  const totalPayout = payoutRows.reduce((sum, row) => sum + row.totalAmount, 0);
  const impactedStakeholders = new Set(payoutRows.map((row) => row.stakeholderId)).size;

  const reviewRows: ReviewPayoutRow[] = payoutRows.map((row) => ({
    dividendPerShare: row.dividendPerShare ?? 0,
    files: row.filesLinks ?? [],
    shareAmount: row.shares ?? 0,
    shareClass: row.shareClassName ?? EMPTY_VALUE,
    stakeholder: row.stakeholderName,
    totalPayout: row.totalAmount,
  }));

  const impactedCount = (shareClassId: null | string): number =>
    new Set(
      payoutRows.filter((row) => row.shareClassId === shareClassId).map((row) => row.stakeholderId),
    ).size;

  const headerSubtitle = [
    getTranslation(isShareClass ? 'PER_SHARE_CLASS' : 'PER_STAKEHOLDER'),
    periodCovered,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Sheet onOpenChange={(open) => !open && onClose()} open={isOpen}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={onClose}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-[72px] w-full items-center justify-between gap-3 bg-white px-6">
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-xl font-semibold leading-8 text-gray-700">
                <AppFormattedMessage id="VIEW_DIVIDEND" />
              </span>
              <span className="truncate text-xs font-[450] leading-4 text-gray-500">
                {headerSubtitle}
              </span>
            </div>
            <CloseModalButton onClose={onClose} />
          </div>

          {/* Same content/footer split as the add-dividend sheet — the gap keeps the footer off
              the panel's bottom edge instead of hugging it. */}
          <div className="flex h-full flex-col gap-4 overflow-hidden">
            <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-6 pt-4">
              <SummaryHighlights
                impactedStakeholders={impactedStakeholders}
                onReview={() => setReviewOpen(true)}
                totalPayout={formatAmount(totalPayout)}
              />

              <SummarySection title={getTranslation('GENERAL')}>
                <SummaryRow label={getTranslation('PAYOUT_NAME')} value={payout.name} />
                <SummaryRow label={getTranslation('PERIOD_COVERED')} value={periodCovered} />
                <SummaryRow
                  label={getTranslation('DISTRIBUTION')}
                  value={getTranslation(isShareClass ? 'PER_SHARE_CLASS' : 'PER_STAKEHOLDER')}
                />
              </SummarySection>

              {payout.items.map((item) => (
                <SummarySection
                  key={item.id}
                  strong
                  title={
                    isShareClass
                      ? shareClassName(item.shareClassId)
                      : stakeholderName(item.stakeholderId)
                  }
                >
                  {!isShareClass && (
                    <SummaryRow
                      label={getTranslation('SHARE_CLASS')}
                      value={shareClassName(item.shareClassId)}
                    />
                  )}
                  <SummaryRow
                    label={getTranslation('DIVIDEND_PER_SHARE')}
                    value={item.amountPerShare ?? EMPTY_VALUE}
                  />
                  {isShareClass && (
                    <SummaryRow
                      label={getTranslation('IMPACTED_STAKEHOLDERS')}
                      value={impactedCount(item.shareClassId)}
                    />
                  )}
                  <SummaryRow
                    highlight
                    label={getTranslation('TOTAL_DIVIDEND_PAYOUT')}
                    value={formatAmount(itemPayout(item))}
                  />
                </SummarySection>
              ))}

              {payout.additionalNotes && (
                <SummarySection title={getTranslation('NOTES')}>
                  <SummaryRow
                    hideColon
                    label={getTranslation('ADDITIONAL_NOTES')}
                    value={
                      <span className="block max-h-40 overflow-y-auto whitespace-pre-line">
                        {payout.additionalNotes}
                      </span>
                    }
                  />
                </SummarySection>
              )}
            </div>

            <div className="flex w-full shrink-0 items-center justify-end px-6">
              <Button
                className="h-9 w-fit px-4 py-2.5 text-sm font-[550] text-white"
                onClick={onClose}
              >
                <AppFormattedMessage id="CLOSE" />
              </Button>
            </div>
          </div>
        </div>

        <DividendReviewPayoutModal
          companyId={companyId}
          currencySymbol={currencySymbol}
          isExporting={isExportingPayout}
          onClose={() => setReviewOpen(false)}
          onExport={() => exportPayout({ payoutId: payout.id })}
          open={reviewOpen}
          period={periodCovered}
          rows={reviewRows}
          subtitle={[payout.name, periodCovered].filter(Boolean).join(' · ')}
        />
      </SheetContent>
    </Sheet>
  );
};
